import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CustomerCombobox } from "@/components/CustomerCombobox";
import { VoiceInputButton } from "@/components/VoiceInputButton";
import { WeekPlanButton } from "@/components/tasks/WeekPlanMenu";
import { TaskAttachments } from "@/components/tasks/TaskAttachments";

const PRIORITIES = ["Hoch", "Mittel", "Niedrig"];
const STATUSES = ["Offen", "In Bearbeitung", "Erledigt"];

export function TaskDialog({
  open,
  onOpenChange,
  editTask,
  formData,
  setFormData,
  onSubmit,
  saving,
  users,
  customers,
  onPlanWeek,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editTask ? "Aufgabe bearbeiten" : "Neue Aufgabe"}
          </DialogTitle>
          <DialogDescription>
            {editTask 
              ? "Ändern Sie die Aufgabendetails."
              : "Erstellen Sie eine neue Aufgabe."
            }
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Titel *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Aufgabentitel"
                required
                data-testid="task-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="description">Beschreibung</Label>
                <VoiceInputButton
                  label="Diktieren"
                  onTranscript={(text) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: (prev.description ? prev.description + " " : "") + text,
                    }))
                  }
                />
              </div>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Aufgabenbeschreibung..."
                rows={3}
                data-testid="task-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="due_date">Fälligkeitsdatum</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  data-testid="task-due-date-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="priority">Priorität</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(v) => setFormData({ ...formData, priority: v })}
                >
                  <SelectTrigger data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => setFormData({ ...formData, status: v })}
              >
                <SelectTrigger data-testid="task-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="assigned_to">Zugewiesen an</Label>
              <Select 
                value={formData.assigned_to_user_id || "none"} 
                onValueChange={(v) => setFormData({ ...formData, assigned_to_user_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="task-assigned-select">
                  <SelectValue placeholder="Mitarbeiter auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nicht zugewiesen</SelectItem>
                  <SelectItem value="general" data-testid="task-assigned-general">Allgemein (beide)</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="customer">Kunde</Label>
              <CustomerCombobox
                customers={customers}
                value={formData.customer_id || ""}
                onChange={(v) => setFormData({ ...formData, customer_id: v })}
                placeholder="Kunde auswählen oder suchen..."
                testid="task-customer-combobox"
              />
            </div>

            <TaskAttachments
              taskId={editTask?.id}
              onIdsChange={(ids) => setFormData((prev) => ({ ...prev, attachment_ids: ids }))}
            />
          </div>
          
          <DialogFooter>
            {editTask && onPlanWeek && editTask.status !== "Erledigt" && (
              <div className="mr-auto">
                <WeekPlanButton
                  task={editTask}
                  onPlanWeek={(t, dateKey, scope) => { onPlanWeek(t, dateKey, scope); onOpenChange(false); }}
                />
              </div>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={saving} data-testid="save-task-btn">
              {saving ? "Speichern..." : "Speichern"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
